import React, { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router';
import { useSetPresetMutation, useUnpickPresetMutation } from '@/services/configurator/configurator.hooks';
import { setSelection } from '@/services/configurator/configurator.api';
import { ConfiguratorData } from '@/services/configurator';
import { configuratorKeys } from '@/services/configurator/configurator.queryKeys';
import { useOptionConfigurator } from './OptionConfiguratorContext';
import { ConfiguratorMutationProvider, ConfiguratorMutationContextType } from './ConfiguratorMutationContext';

// Wraps the existing API mutation hooks for the authenticated buyer flow and exposes them via
// ConfiguratorMutationContext. The Showroom provides a different implementation that writes to
// localStorage and calls the public /calculate endpoint.
//
// Provider always succeeds. If selectedDeadlineId is null (early render, before deadline picked),
// the mutation functions throw on invocation. Components shouldn't be calling mutations before a
// deadline is selected anyway.
export const ApiMutationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { residentialUnitId, selectedDeadlineId } = useOptionConfigurator();
    // ApiMutationProvider is mounted ABOVE the `/room/:roomId` route, so its own useParams never
    // sees a roomId. Callers (OptionSelectButton) pass `params.roomId` explicitly; we fall back
    // to this param for paths where the provider is itself under a room route.
    const { roomId: roomIdParam } = useParams<{ roomId?: string }>();
    const queryClient = useQueryClient();

    const setPresetMutation = useSetPresetMutation();
    const unpickPresetMutation = useUnpickPresetMutation();
    const [isSelecting, setIsSelecting] = useState(false);

    const value = useMemo<ConfiguratorMutationContextType>(() => {
        // Resolve the deadline for a mutation: an explicit per-call override (used by the legacy
        // multi-deadline page) wins, otherwise fall back to the deadline tracked in context.
        const requireDeadline = (override?: number | null): number => {
            const deadlineId = override ?? selectedDeadlineId;
            if (!deadlineId) {
                throw new Error('Cannot mutate configurator state: no deadline selected');
            }
            return deadlineId;
        };

        const resolveRoomId = (override: number | null | undefined): number | null => {
            if (override !== undefined) return override;
            if (!roomIdParam) return null;
            return roomIdParam === '0' ? null : Number(roomIdParam);
        };

        // Optimistically move a group's selection in the cache the configurator reads
        // (`allDeadlinesByUnit`), so the selection indicator updates instantly — before the backend
        // roundtrip. The invalidate/refetch that follows each mutation reconciles to the
        // authoritative state (and reverts this if the call fails). `selectedOptionId === null`
        // clears the group.
        const patchOptimisticSelection = (
            groupId: number,
            roomId: number | null,
            deadlineId: number,
            selectedOptionId: number | null
        ) => {
            queryClient.setQueryData<ConfiguratorData>(
                configuratorKeys.allDeadlinesByUnit(residentialUnitId),
                (old) => {
                    if (!old) return old;
                    const groups = old.groups.map((g) => {
                        if (g.id !== groupId || g.deadlineId !== deadlineId || (g.roomId ?? null) !== (roomId ?? null)) {
                            return g;
                        }
                        const options = g.options.map((o) => ({ ...o, selected: o.id === selectedOptionId }));
                        return { ...g, options, selectedOption: options.find((o) => o.selected) ?? null };
                    });
                    return { ...old, groups };
                }
            );
        };

        const runSelectOne = async (option: Parameters<ConfiguratorMutationContextType['selectOption']>[0]) => {
            const deadlineId = requireDeadline(option.deadlineId);
            const roomId = resolveRoomId(option.roomId);
            setIsSelecting(true);
            patchOptimisticSelection(option.option.optionGroupId, roomId, deadlineId, option.option.id);
            try {
                const data = await setSelection(residentialUnitId, deadlineId, {
                    GroupId: option.option.optionGroupId,
                    SelectedOptionId: option.option.id,
                    RoomId: roomId,
                    CustomFieldData: option.customFieldData ?? null,
                    ManualQuantity: option.quantity ?? null,
                });
                queryClient.setQueryData(configuratorKeys.byUnitAndDeadline(residentialUnitId, deadlineId), data);
            } finally {
                // Reconcile to authoritative state (also reverts the optimistic patch on failure).
                queryClient.invalidateQueries({ queryKey: configuratorKeys.allDeadlinesByUnit(residentialUnitId) });
                setIsSelecting(false);
            }
        };

        const runClearOne = async (params: Parameters<ConfiguratorMutationContextType['clearSelection']>[0]) => {
            const deadlineId = requireDeadline(params.deadlineId);
            const roomId = resolveRoomId(params.roomId);
            setIsSelecting(true);
            patchOptimisticSelection(params.groupId, roomId, deadlineId, null);
            try {
                const data = await setSelection(residentialUnitId, deadlineId, {
                    GroupId: params.groupId,
                    SelectedOptionId: null,
                    RoomId: roomId,
                    CustomFieldData: null,
                    // Clearing the group drops any buyer-entered quantity along with the selection.
                    ManualQuantity: null,
                });
                queryClient.setQueryData(configuratorKeys.byUnitAndDeadline(residentialUnitId, deadlineId), data);
            } finally {
                queryClient.invalidateQueries({ queryKey: configuratorKeys.allDeadlinesByUnit(residentialUnitId) });
                setIsSelecting(false);
            }
        };

        return {
            selectOption: (params) => {
                // Fire-and-forget; UI shows in-flight state via isSelecting.
                void runSelectOne(params);
            },
            clearSelection: (params) => {
                void runClearOne(params);
            },
            selectOptions: async (selections) => {
                if (selections.length === 0) return;
                setIsSelecting(true);
                // Optimistically reflect the whole batch in the cache the view reads
                // (`allDeadlinesByUnit`) up front, so the selection indicators — and any dialog that
                // closes on resolve — see the final state immediately, without waiting for the
                // roundtrip. The reconcile refetch below confirms it (or reverts on failure). Mirrors
                // `runSelectOne`; without it the view only changed on the late refetch, flashing the
                // just-closed dependency dialog.
                for (const { option, roomId, deadlineId: override } of selections) {
                    patchOptimisticSelection(
                        option.optionGroupId,
                        resolveRoomId(roomId),
                        requireDeadline(override),
                        option.id
                    );
                }
                try {
                    // A batch (e.g. the legacy "identical options across groups" feature) may span
                    // several deadlines, so resolve each selection's deadline independently and patch
                    // its per-deadline cache. The all-deadlines query is invalidated once at the end.
                    for (const { option, customFieldData, roomId, quantity, deadlineId: override } of selections) {
                        const deadlineId = requireDeadline(override);
                        const data = await setSelection(residentialUnitId, deadlineId, {
                            GroupId: option.optionGroupId,
                            SelectedOptionId: option.id,
                            RoomId: resolveRoomId(roomId),
                            CustomFieldData: customFieldData ?? null,
                            ManualQuantity: quantity ?? null,
                        });
                        queryClient.setQueryData(
                            configuratorKeys.byUnitAndDeadline(residentialUnitId, deadlineId),
                            data
                        );
                    }
                } finally {
                    // Reconcile to authoritative state (also reverts the optimistic patch on failure).
                    queryClient.invalidateQueries({ queryKey: configuratorKeys.allDeadlinesByUnit(residentialUnitId) });
                    setIsSelecting(false);
                }
            },
            isSelecting,
            pickStyle: (styleId, roomId, deadlineId) => {
                const resolved = requireDeadline(deadlineId);
                setPresetMutation.mutate([residentialUnitId, resolved, styleId, roomId]);
            },
            isPickingStyle: setPresetMutation.isPending,
            unpickStyle: (styleGroupId, roomId) => {
                // The backend unpick route has no deadline segment, so unlike pickStyle it
                // takes no deadlineId — see unpickStyle in configurator.api.ts.
                unpickPresetMutation.mutate([residentialUnitId, styleGroupId, roomId]);
            },
            isUnpickingStyle: unpickPresetMutation.isPending,
        };
    }, [
        isSelecting,
        setPresetMutation,
        unpickPresetMutation,
        residentialUnitId,
        selectedDeadlineId,
        roomIdParam,
        queryClient,
    ]);

    return <ConfiguratorMutationProvider value={value}>{children}</ConfiguratorMutationProvider>;
};
